"use client";

import {useState} from "react";
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import FrozenStep from "./FrozenStep";
import FrozenValue from "./FrozenValue";
import {TextDiffSide} from "./ProposalDiff";
import {diffUseCaseSteps} from "./diffUseCaseSteps";
import {CHANGE_BADGE, isUseCaseStep} from "./useCaseSnapshot";

const COUNT_CLASS = {
   added: "text-green-700 dark:text-green-300",
   removed: "text-red-700 dark:text-red-300",
   edited: "text-500",
};

/** Pair related steps in one row so changes never drift into unrelated paragraphs. */
export default function UseCaseStepsDiff({before, after, scriptNames}: {
   before: unknown[];
   after: unknown[];
   scriptNames: ReadonlyMap<string, string>;
}) {
   const {t} = useTranslation("ask");
   const [showUnchanged, setShowUnchanged] = useState(false);
   const changes = diffUseCaseSteps(before, after);
   const fromIndex = new Map([...changes.pairs].map(([from, to]) => [to, from]));
   const rows: {from?: number; to?: number}[] = [];
   const removals = new Set(before.map((_, index) => index).filter(index => !changes.pairs.has(index)));
   after.forEach((_, to) => {
      const from = fromIndex.get(to);
      // Keep removals near their original neighbours; the proposed order leads the comparison.
      if (from !== undefined) for (const removed of removals) {
         if (removed < from) { rows.push({from: removed}); removals.delete(removed); }
      }
      rows.push({from, to});
   });
   removals.forEach(from => rows.push({from}));
   const unchanged = rows.filter(({to}) => to !== undefined && changes.after[to].kind === "unchanged" && !changes.after[to].moved).length;
   const counts = {
      edited: changes.after.filter(change => change.kind === "edited").length,
      added: changes.after.filter(change => change.kind === "added").length,
      removed: changes.before.filter(change => change.kind === "removed").length,
   };

   return <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
         {(["edited", "added", "removed"] as const).filter(kind => counts[kind] > 0).map(kind =>
            <span key={kind} className={clsx("font-medium", COUNT_CLASS[kind])}>
               {t(`toolApproval.stepCount.${kind}`, {count: counts[kind]})}
            </span>)}
         {unchanged > 0 && <label className="ml-auto flex cursor-pointer items-center gap-2 py-1 text-600">
            <input type="checkbox" checked={showUnchanged} onChange={event => setShowUnchanged(event.target.checked)} className="h-3.5 w-3.5 rounded border-300 text-primary focus:ring-primary" />
            {t("toolApproval.showUnchangedSteps", {count: unchanged})}
         </label>}
      </div>
      <div className="overflow-hidden rounded-xl border border-neutral">
         <div className="hidden grid-cols-2 border-b border-neutral bg-100/60 @[36rem]:grid">
            {(["before", "after"] as const).map(side => <div key={side} className="px-4 py-2.5 text-xs font-semibold text-600">{t(`toolApproval.${side}`)}</div>)}
         </div>
         <ol className="divide-y divide-neutral">
            {rows.map(({from, to}) => {
               const change = to === undefined ? changes.before[from!] : changes.after[to];
               if (change.kind === "unchanged" && !change.moved && !showUnchanged) return null;
               const left = from === undefined ? undefined : before[from];
               const right = to === undefined ? undefined : after[to];
               return <li key={`${from ?? "new"}:${to ?? "removed"}`} data-change={change.kind} className="grid @[36rem]:grid-cols-2">
                  {(["before", "after"] as const).map(side => {
                     const index = side === "before" ? from : to;
                     const item = side === "before" ? left : right;
                     const missing = index === undefined;
                     return <div key={side} data-side={side} className={clsx("min-w-0 px-4 py-3 text-sm leading-6", side === "after" && "border-t border-neutral @[36rem]:border-l @[36rem]:border-t-0",
                        missing && "hidden bg-100/30 @[36rem]:block")}>
                        {missing ? <span className="text-xs italic text-400">{t(`toolApproval.${side === "before" ? "newStep" : "stepRemoved"}`)}</span> : <>
                           <div className="mb-2 flex flex-wrap items-center gap-2 text-xs font-medium text-500">
                              <span className="@[36rem]:hidden">{t(`toolApproval.${side}`)} ·</span>
                              <span>{t("toolApproval.stepNumber", {number: index + 1})}</span>
                              {(change.kind === "added" || change.kind === "removed") && <span className={clsx("rounded px-1.5 py-0.5", CHANGE_BADGE[change.kind])}>{t(`toolApproval.${change.kind}`)}</span>}
                              {change.moved && <span>{t("toolApproval.moved", change.moved)}</span>}
                           </div>
                           {isUseCaseStep(item) ? <FrozenStep hideId step={item} scriptNames={scriptNames}
                              descriptionContent={isUseCaseStep(left) && isUseCaseStep(right)
                                 ? <TextDiffSide before={left.description} after={right.description} side={side} /> : undefined} />
                              : <FrozenValue value={item} scriptNames={scriptNames} />}
                        </>}
                     </div>;
                  })}
               </li>;
            })}
         </ol>
      </div>
   </div>;
}
